import { fetchStudyQueue } from './study';
import { StudyCardItem, StudyQueueCounts, StudyQueueResponse } from '../types/study';

export type StudyMode = 'learn' | 'review';

export interface QueueSnapshot {
  mode: StudyMode;
  items: StudyCardItem[];
  counts: StudyQueueCounts;
  receivedAt: number;
  expiresAt: number;
  epoch: number;
}

interface QueueRequest {
  controller: AbortController;
  promise: Promise<QueueSnapshot>;
}

// The cache is scoped to one mounted study page, never persisted across accounts.
export class StudyQueueCache {
  private epoch = 0;
  private snapshots = new Map<StudyMode, QueueSnapshot>();
  private requests = new Map<StudyMode, QueueRequest>();

  get(mode: StudyMode): QueueSnapshot | undefined {
    const snapshot = this.snapshots.get(mode);
    return snapshot?.epoch === this.epoch && snapshot.expiresAt > Date.now() ? snapshot : undefined;
  }

  load(mode: StudyMode, force = false): Promise<QueueSnapshot> {
    if (!force) {
      const cached = this.get(mode);
      if (cached) return Promise.resolve(cached);
      const pending = this.requests.get(mode);
      if (pending) return pending.promise;
    }

    this.requests.get(mode)?.controller.abort();
    this.snapshots.delete(mode);
    const controller = new AbortController();
    const epoch = this.epoch;
    const promise: Promise<QueueSnapshot> = fetchStudyQueue(undefined, 30, mode, controller.signal)
      .then((data: StudyQueueResponse) => {
        if (controller.signal.aborted || epoch !== this.epoch || this.requests.get(mode)?.promise !== promise) {
          throw new DOMException('Superseded study queue', 'AbortError');
        }
        const receivedAt = Date.now();
        const nextDay = data.next_day_at ? Date.parse(data.next_day_at) : Number.POSITIVE_INFINITY;
        const ttl = data.items?.length ? 30_000 : 5_000;
        const snapshot: QueueSnapshot = {
          mode,
          items: data.items || [],
          counts: data.counts || {},
          receivedAt,
          expiresAt: Math.min(receivedAt + ttl, Number.isFinite(nextDay) ? nextDay : Number.POSITIVE_INFINITY),
          epoch,
        };
        this.snapshots.set(mode, snapshot);
        return snapshot;
      })
      .finally(() => {
        if (this.requests.get(mode)?.promise === promise) this.requests.delete(mode);
      });
    this.requests.set(mode, { controller, promise });
    return promise;
  }

  // A local edit keeps the original expiry; switching tabs cannot keep it alive forever.
  remember(mode: StudyMode, items: StudyCardItem[], counts: StudyQueueCounts, source: QueueSnapshot | null, afterMutation = false): QueueSnapshot | null {
    if (!source || source.mode !== mode || source.expiresAt <= Date.now() || (!afterMutation && source.epoch !== this.epoch)) return null;
    const snapshot = { ...source, items, counts, epoch: this.epoch };
    this.snapshots.set(mode, snapshot);
    return snapshot;
  }

  invalidate() {
    this.epoch += 1;
    this.snapshots.clear();
    for (const request of this.requests.values()) request.controller.abort();
    this.requests.clear();
  }
}
